import { Document, Page, Text, StyleSheet, Font, View, Image } from '@react-pdf/renderer';
import AchemedLogo from '../assets/img/achemed-simbolo-azul.png'

Font.register({
  family: "Helvetica",
  fonts: [
    {
      src: "https://fonts.gstatic.com/s/helveticaneue/v70/1Ptsg8zYS_SKggPNyCg4TYFv.ttf",
      fontWeight: "normal",
    },
    {
      src: "https://fonts.gstatic.com/s/helveticaneue/v70/1Ptsg8zYS_SKggPNyCg4QIFv.ttf",
      fontWeight: "bold",
    },
  ],
});

const styles = StyleSheet.create({
  page: {
    padding: 15,
    fontSize: 10,
    fontFamily: "Helvetica",
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 5,
    alignItems: "center",
  },
  logo: {
    width: 60,
    height: 60,
    marginRight: 10
  },
  titleSection: {
    textAlign: "right",
    flexGrow: 1,
  },
  title: {
    fontSize: 18,
    fontWeight: "bold",
    marginBottom: 6,
  },
  subtitle: {
    fontSize: 12,
  },
  footer: {
    position: 'absolute',
    bottom: 30,
    left: 30,
    right: 30,
    textAlign: 'center',
    fontSize: 10,
  },
  table: {
    display: "table",
    width: "100%",
    marginTop: 5,
    marginBottom: 5,
  },
  tableRow: {
    flexDirection: "row",
  },
  tableHeader: {
    fontWeight: "bold",
    padding: 5,
    flex: 1,
    textAlign: "center",
    fontSize: 12,
  },
  tableCell: {
    padding: 7,
    flex: 1,
    textAlign: "center",
    fontSize: 9,
  },
  lastColumn: {
    borderRightWidth: 0,
  },
});

// Function to split medicines into pages
const splitIntoPages = (medicines, itemsPerPage = 18) => {
  const pages = [];
  for (let i = 0; i < medicines.length; i += itemsPerPage) {
    pages.push(medicines.slice(i, i + itemsPerPage));
  }
  return pages;
};

const mapUnit = (unit) => {
  const unitMap = {
      'Und': 'Unidade',
      'Cx': 'Caixa',
      'Frs': 'Frasco',
      'L': 'Litro',
      'ml': 'Mililitro (ml)',
      'mg': 'Miligrama',
      'Kg': 'Quilograma',
      'g': 'Grama',
      'Amp': 'Ampola',
      'Bg': 'Bisnaga',
      'Bl': 'Blister (es)',
      'Ct': 'Cartela',
      'Cp': 'Comprimido',
      'Cj': 'Conjunto',
      'Env': 'Envelope',
      'Fl': 'Flaconete',
      'Kt': 'Kit',
      'Lt': 'Lata',
      'Pct': 'Pacote',
      'Pt': 'Pote',
      'Tb': 'Tubo',
      'Vd': 'Vidro',
      'Drg': 'Drágea',
      'Cap': 'Cápsula',
      'Sup': 'Supositório',
      'Sch': 'Sachê',
      'Ovu': 'Óvulo',
      'Pst': 'Pasta (g)',
      'AdT': 'Adesivo transdérmico',
      'Xp': 'Xarope (ml)',
      'Elx': 'Elixir (ml)',
      'Pmd': 'Pomada (g)',
      'Crm': 'Creme (g)',
      'Gel': 'Gel (g)',
      'Lo': 'Loção (g)',
      'Ugn': 'Unguento (g)',
      'Crt': 'Cerato (g)',
  };
  return unitMap[unit] || unit;
}

// Table headers component
const TableHeaders = () => (
  <View style={styles.tableRow}>
    {[
      "Código",
      "Nome",
      "Nome(s) comercial(is)",
      "Apresentação",
      "Unidade",
      "Tipo"
    ].map((header, index, array) => (
      <Text
        key={index}
        style={[
          styles.tableHeader,
          index === array.length - 1 ? styles.lastColumn : {},
          (index === 1 || index === 2) ? { flex: 2 } : {}, 
        ]}>
        {header}
      </Text>
    ))}
  </View>
);

// Single medicine row component
const MedicineRow = ({ medicine }) => (
  <View style={styles.tableRow}>
    <Text style={styles.tableCell}>{medicine.external_code || '-'}</Text>
    <Text style={[styles.tableCell, { flex: 2, textAlign: 'left' }]}>{medicine.name}</Text>
    {
      medicine.business_name ? (
        <Text style={[styles.tableCell, { flex: 2, textAlign: 'left' }]}>{medicine.business_name}</Text>
      ) : (
        <Text style={[styles.tableCell, { flex: 2 }]}>-</Text>
      )
    }
    <Text style={styles.tableCell}>{medicine.presentation || '-'}</Text>
    <Text style={styles.tableCell}>{mapUnit(medicine.unit) || '-'}</Text>
    <Text style={styles.tableCell}>{medicine.type || '-'}</Text>
  </View>
);

// Header component that appears on every page
const Header = () => (
  <View style={styles.header}>
    <Image style={styles.logo} src={AchemedLogo} />
    <View style={styles.titleSection}>
      <Text style={styles.title}>Relatório de Medicamentos em falta</Text>
      <Text style={styles.subtitle}>
        Gerado em {new Date().toLocaleDateString()} - {new Date().toLocaleTimeString()}
      </Text>
    </View>
  </View>
);

// Page footer with pagination
const Footer = ({ pageNumber, totalPages }) => (
  <View style={styles.footer}>
    <Text>
      Página {pageNumber} de {totalPages}
    </Text>
  </View>
);

export const PDFReport = ({ medicines }) => {
  const medicinePages = splitIntoPages(medicines);
  const totalPages = medicinePages.length || 1; // At least 1 page even if empty

  return (
    <Document>
      {medicinePages.length > 0 ? (
        medicinePages.map((pageMedicines, pageIndex) => (
          <Page 
            key={pageIndex} 
            size="A4" 
            orientation="landscape" 
            style={styles.page}
          >
            <Header />
            
            <View style={styles.table}>
              <TableHeaders />
              {pageMedicines.map((medicine, index) => (
                <MedicineRow key={index} medicine={medicine} />
              ))}
            </View>
            
            <Footer pageNumber={pageIndex + 1} totalPages={totalPages} />
          </Page>
        ))
      ) : (
        <Page size="A4" orientation="landscape" style={styles.page}>
          <Header />
          
          <View style={styles.table}>
            <TableHeaders />
            <View style={styles.tableRow}>
              <Text style={[styles.tableCell, { flex: 6 }]}>
                Nenhum medicamento em falta encontrado.
              </Text>
            </View>
          </View>
          
          <Footer pageNumber={1} totalPages={1} />
        </Page>
      )}
    </Document>
  );
};